import { Table, TableBody, TableCell, TableRow } from "@material-ui/core";
import React, { useState } from "react";

import { EditIcon } from "../components/icons/themes/windows10";
import { PropertySensitivityLevels } from "../types/global";
import {
  allowlistBuiltInProps,
  PHIAllowListEditModal,
} from "./PHIAllowListEditModal";
import { AllowList, AllowListType } from "./types";

interface PHIAllowListContainerProps {
  sensitivityLevels: Record<AllowListType, PropertySensitivityLevels>;
  onSave: (
    allowListType: AllowListType,
    newSensitivityLevels: PropertySensitivityLevels
  ) => Promise<void>;
}

export const PHIAllowListContainer: React.FC<PHIAllowListContainerProps> = ({
  sensitivityLevels,
  onSave,
}) => {
  return (
    <Table>
      <TableBody>
        <PHIEventAllowList
          allowListType={AllowListType.Event}
          allowListName="Event Properties"
          sensitivityLevels={sensitivityLevels.event}
          onSave={onSave}
        />
        <PHIEventAllowList
          allowListType={AllowListType.User}
          allowListName="User Properties"
          sensitivityLevels={sensitivityLevels.user}
          onSave={onSave}
        />
        <PHIEventAllowList
          allowListType={AllowListType.Group}
          allowListName="Group Properties"
          sensitivityLevels={sensitivityLevels.group}
          onSave={onSave}
        />
      </TableBody>
    </Table>
  );
};

function beautifyPropName(propName: string): string {
  return allowlistBuiltInProps[propName]?.beautifulName ?? `"${propName}"`;
}

const previewAllowList: (allowList: AllowList) => string = (allowList) => {
  if (allowList.length == 0) {
    return "No allowlisted properties";
  } else if (allowList.length == 1) {
    return `${beautifyPropName(allowList[0])} allowlisted`;
  } else if (allowList.length == 2) {
    return `${beautifyPropName(allowList[0])} and ${beautifyPropName(
      allowList[1]
    )} allowlisted`;
  } else {
    return `${beautifyPropName(allowList[0])}, ${beautifyPropName(
      allowList[1]
    )}, and ${allowList.length - 2} other properties allowlisted`;
  }
};

interface PHIEventAllowListProps {
  allowListType: AllowListType;
  allowListName: string;
  sensitivityLevels: PropertySensitivityLevels;
  onSave: (
    allowListType: AllowListType,
    newSensitivityLevels: PropertySensitivityLevels
  ) => Promise<void>;
}

const PHIEventAllowList: React.FC<PHIEventAllowListProps> = ({
  allowListType,
  allowListName,
  sensitivityLevels,
  onSave,
}) => {
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);

  const propPreview = previewAllowList(Object.keys(sensitivityLevels));

  return (
    <>
      <TableRow key={allowListType} className="group hover:bg-gray-100">
        <TableCell>
          <div className="text-gray-600 font-bold text-sm truncate">
            {allowListName}
          </div>
        </TableCell>
        <TableCell>{propPreview}</TableCell>
        <TableCell align="center">
          <div className="flex">
            <div
              className="mr-1 bg-white rounded-full hover:bg-purple-400 fill-gray-700 hover:text-white group cursor-pointer flex justify-center items-center w-8 h-8"
              onClick={() => setIsEditModalOpen(true)}
            >
              <EditIcon />
            </div>
          </div>
        </TableCell>
      </TableRow>
      {isEditModalOpen && (
        <PHIAllowListEditModal
          open={isEditModalOpen}
          allowListType={allowListName}
          propertySensitivityLevels={sensitivityLevels}
          onSave={async (newSensitivityLevels) => {
            await onSave(allowListType, newSensitivityLevels);
            setIsEditModalOpen(false);
          }}
          onClose={() => setIsEditModalOpen(false)}
        />
      )}
    </>
  );
};
